import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FolderKanban } from 'lucide-react';
import {
  FormDateField,
  FormDialog,
  FormSection,
  FormStatusField,
  FormTextField,
} from '@/components/common/FormDialog';
import { useSaveMarketProject } from '../hooks/useMpP2Mutations';
import {
  marketProjectFormSchema,
  type MarketProject,
  type MarketProjectFormValues,
  type UpdateMarketProjectPayload,
} from '../types/marketPromotionP2.types';

const NEW_PROJECT_VALUES: MarketProjectFormValues = {
  name: '',
  code: '',
  startDate: '',
  deadlineDate: '',
  isActive: true,
};

function getSavedValues(project: MarketProject): MarketProjectFormValues {
  return {
    name: project.name ?? '',
    code: project.code ?? '',
    startDate: project.startDate?.slice(0, 10) ?? '',
    deadlineDate: project.deadlineDate?.slice(0, 10) ?? '',
    isActive: project.isActive,
  };
}

function toUpdatePayload(values: MarketProjectFormValues): UpdateMarketProjectPayload {
  return {
    name: values.name.trim(),
    isActive: values.isActive,
    ...(values.startDate === '' ? {} : { startDate: values.startDate }),
    ...(values.deadlineDate === '' ? {} : { deadlineDate: values.deadlineDate }),
  };
}

interface MarketProjectFormDialogProps {
  project: MarketProject | null;
  onClose: () => void;
}

export function MarketProjectFormDialog({ project, onClose }: MarketProjectFormDialogProps) {
  const isEditMode = project !== null;
  const form = useForm<MarketProjectFormValues>({
    resolver: zodResolver(marketProjectFormSchema),
    defaultValues: isEditMode ? getSavedValues(project) : NEW_PROJECT_VALUES,
  });
  const save = useSaveMarketProject(onClose);

  function handleSave(values: MarketProjectFormValues) {
    const payload = toUpdatePayload(values);
    if (isEditMode) {
      save.mutate({ id: project.id, payload });
      return;
    }
    const code = values.code.trim();
    save.mutate({ id: null, payload: { ...payload, ...(code === '' ? {} : { code }) } });
  }

  return (
    <FormDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      icon={FolderKanban}
      title={isEditMode ? 'Edit Project' : 'Add Project'}
      description={
        isEditMode
          ? `Code ${project.code ?? '-'}. You cannot change the code.`
          : 'A donation project that budgets are made for.'
      }
      form={form}
      isPending={save.isPending}
      isEditMode={isEditMode}
      onSubmit={handleSave}
    >
      <FormSection>
        <FormTextField
          control={form.control}
          name="name"
          label="Name"
          placeholder="General Cash Collection FY2027-2028"
          isFullRow
        />
        {isEditMode ? null : (
          <FormTextField
            control={form.control}
            name="code"
            label="Code"
            placeholder="GCC27-28"
            hasTabularNumbers
            isFullRow
          />
        )}
        <FormDateField control={form.control} name="startDate" label="Start Date" />
        <FormDateField control={form.control} name="deadlineDate" label="Deadline" />
      </FormSection>
      <FormStatusField control={form.control} name="isActive" />
    </FormDialog>
  );
}
